import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Activity, Clock, Database } from 'lucide-react';

interface PerfSummary {
  function_name: string;
  avg_ms: number;
  max_ms: number;
  call_count: number;
}

const PerformanceCard: React.FC = () => {
  const { data: perfData = [], isLoading } = useQuery({
    queryKey: ['performance-logs-summary'],
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('performance_logs')
        .select('function_name, execution_ms, created_at')
        .gte('created_at', since)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      const grouped: Record<string, { total: number; max: number; count: number }> = {};
      data.forEach((row: any) => {
        const fn = row.function_name;
        if (!grouped[fn]) grouped[fn] = { total: 0, max: 0, count: 0 };
        const ms = Number(row.execution_ms);
        grouped[fn].total += ms;
        grouped[fn].max = Math.max(grouped[fn].max, ms);
        grouped[fn].count += 1;
      });

      return Object.entries(grouped).map(([fn, stats]) => ({
        function_name: fn,
        avg_ms: Math.round(stats.total / stats.count),
        max_ms: Math.round(stats.max),
        call_count: stats.count,
      })) as PerfSummary[];
    },
    refetchInterval: 60000,
  });

  const getHealthColor = (avgMs: number) => {
    if (avgMs < 200) return 'text-green-600';
    if (avgMs < 500) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4" />
          Query Performance (24h)
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : perfData.length === 0 ? (
          <p className="text-sm text-muted-foreground">No performance data yet. Data will appear after queries are executed.</p>
        ) : (
          <div className="space-y-3">
            {perfData.map((item) => (
              <div key={item.function_name} className="flex items-center justify-between border-b pb-2 last:border-0">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium font-mono">{item.function_name}</p>
                  <p className="text-xs text-muted-foreground">{item.call_count} calls</p>
                </div>
                <div className="text-right space-y-0.5">
                  <p className={`text-sm font-semibold ${getHealthColor(item.avg_ms)}`}>
                    {item.avg_ms}ms avg
                  </p>
                  <p className="text-xs text-muted-foreground">{item.max_ms}ms max</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PerformanceCard;
